import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import Select from 'react-select';

import {
  Section,
  Container,
  Wrapper,
  H3,
  H5,
  Text12,
  Text10,
} from '../../atoms';


/**
 * A tab is either a single block of rich text (the original shape, still used
 * by the older tabs) or a list of sections, each of which may carry a table.
 *
 * Tables are modelled as data rather than markup because the backoffice editor
 * is Draft.js, and draftjs-to-html cannot emit a <table> — any edit to a tab
 * whose html contained one would silently flatten it. Columns and rows are
 * nested Lists instead, so an editor maintains them like any other content.
 */
function TabBody({ value }) {
  const sections = Array.isArray(value.sections) ? value.sections : [];

  if (sections.length === 0) {
    return (
      <Text
        style={{ padding: 'inherit', margin: 'inherit' }}
        dangerouslySetInnerHTML={{ __html: value.text }}
      />
    );
  }

  return (
    <>
      {sections.map((section, index) => {
        // A column header left empty drops that column, so the same shape
        // serves a two, three or four column table without a stray blank one.
        // The cookie policy needs four (Nome, Tipo, Finalidade, Duração); the
        // privacy policy needs three.
        const headers = [section.col1, section.col2, section.col3, section.col4]
          .map((header) => (header || '').trim());
        const columns = headers.filter((header) => header !== '').length;
        const rows = Array.isArray(section.rows) ? section.rows : [];

        return (
          <div key={String(index)}>
            {section.heading &&
              <SectionHeading dangerouslySetInnerHTML={{ __html: section.heading }} />
            }
            {section.text &&
              <Text
                style={{ padding: 'inherit', margin: 'inherit' }}
                dangerouslySetInnerHTML={{ __html: section.text }}
              />
            }
            {(columns > 0 && rows.length > 0) &&
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      {headers.slice(0, columns).map((header, headerIndex) => (
                        <th key={String(headerIndex)}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      <tr key={String(rowIndex)}>
                        {[row.c1, row.c2, row.c3, row.c4].slice(0, columns).map((cell, cellIndex) => (
                          <td
                            key={String(cellIndex)}
                            dangerouslySetInnerHTML={{ __html: toHtml(cell) }}
                          />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableScroll>
            }
          </div>
        );
      })}
    </>
  );
}

/** Cells are plain text fields, so newlines are the only formatting they carry. */
const toHtml = (value) => String(value || '').replace(/(\r\n|\r|\n)/g, '<br />');

export default function LegalInformation({ list, language, title }) {

  const [dataTabs, setDataTabs] = useState([]);
  const [dataLegal, setDataLegal] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (list && list.length > 0) {
      let arrTabs = [];
      let arrLegal = [];
      for (let i = 0; i < list.length; i++) {
        arrTabs.push({
          index: i,
          value: list[i].key,
          label: list[i].title
        })
        arrLegal.push({
          sectionName: list[i].key,
          title: list[i].title,
          text: list[i].description,
          sections: list[i].sections
        })
      }
      setDataTabs(arrTabs);
      setDataLegal(arrLegal);

      const hash = window.location.hash.replace('#', '');
      if (hash && hash !== '') {
        if (arrLegal && arrLegal.length > 0) {
          let index = arrLegal.findIndex((line) => line.sectionName === hash);
          if (index !== -1) {
            setActive(index);
          }
        }
      }
    }
  }, [list]);

  return (
    <Section noFade>
      <>
        <Banner>
          <Container lg>
            <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <H3
                color='#35383B'
                dangerouslySetInnerHTML={{ __html: title }}
              />
            </div>
          </Container>
        </Banner>
        <ContainerLegal>
          {(dataLegal && dataLegal.length > 0) &&
            <TabsContainer className='desktop'>
              <Tabs selectedIndex={active} onSelect={(index) => setActive(index)}>
                <WrapperTab>
                  <Container lg>
                    <TabInnerPannel>
                      <TabList>
                        {dataTabs.map((value, index) => (
                          <AllTabs key={String(index)}>
                            <Text10>
                              {value.label}
                            </Text10>
                          </AllTabs>
                        ))}
                      </TabList>
                    </TabInnerPannel>
                  </Container>
                </WrapperTab>
                <Wrapper both noHeight>
                  <Container md>
                    {dataLegal.map((value, index) => (
                      <TabPanel key={String(index)}>
                        <TabInnerPannel>
                          <H5
                            color="#368D67"
                            dangerouslySetInnerHTML={{ __html: value.title }}
                          />
                          <Margin />
                          <TabBody value={value} />
                        </TabInnerPannel>
                      </TabPanel>
                    ))}
                  </Container>
                </Wrapper>
              </Tabs>
            </TabsContainer>
          }
          {(dataLegal && dataLegal.length > 0) &&
            <TabsContainer className='mobile'>
              <WrapperTab>
                <Container lg>
                  <DropdownContainer>
                    <Dropdown
                      onChange={options => setActive(options.index)}
                      defaultValue={dataTabs[0]}
                      value={dataTabs[active]}
                      classNamePrefix='dropdown'
                      options={dataTabs}
                    />
                  </DropdownContainer>
                </Container>
              </WrapperTab>
              <Wrapper both noHeight>
                <Container md>
                  {dataLegal.map((value, index) => (
                    <TabInnerPannel
                      key={String(index)}
                      className={(value.sectionName === active || index === active) ? 'show' : 'hide'}
                    >
                      <H5
                        color="#368D67"
                        dangerouslySetInnerHTML={{ __html: value.title }}
                      />
                      <Margin />
                      <TabBody value={value} />
                    </TabInnerPannel>
                  ))}
                </Container>
              </Wrapper>
            </TabsContainer>
          }
        </ContainerLegal>
      </>
    </Section>
  )
}

const Banner = styled.div`
  height:50vh;
  background-color: #5BB990;
  display: flex;
  align-items:center;
`;
const ContainerLegal = styled.div`
  position:relative;
  height: 100%;
`;
const TabsContainer = styled.div`
  @media screen and (min-width:768px){
    &.desktop{
      display:block;
    }
    &.mobile{
      display:none;
    }
  }
  @media screen and (max-width:767px){
    &.desktop{
      display:none;
    }
    &.mobile{
      display:block;
    }
  }
`;
const WrapperTab = styled.div`
  position:relative;
  background-color: #5BB990;
`;
const TabInnerPannel = styled.div`
  &.show {
    display:block;
  }
  &.hide {
    display:none;
  }
`;
const AllTabs = styled(Tab)`
  display:inline-flex;
  margin-right:31px;
  margin-top:25px;
  margin-bottom: 25px;
  cursor:pointer;
  >div{
    transition: all 0.5s ease;
    color: #35383B;
  }
  &:hover,
  &.react-tabs__tab--selected {
    >div{
      color: #236146;
    }
  }
`;
const Margin = styled.div`
  margin-bottom: 8px;
  ${({ medium }) => medium && css`
    margin-bottom: 16px;
  `};
`;
const DropdownContainer = styled.div`
  flex:100%;
  display:flex;
  flex-direction:column;
`;
const Dropdown = styled(Select)`
  flex: 1;
  z-index:2 !important;
  font-size: calc(10px + (12 - 10) * ((100vw - 540px) / (1920 - 540)));
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.29;
  letter-spacing: normal;
  color: #000;
  border-radius: 0;
  margin-bottom: 25px;
  .dropdown__control {
    border-radius: 6px;
    background-color: transparent;
    padding: 0 calc(14px + (29 - 14) * ((100vw - 540px) / (1920 - 540)));
    height: 100%;
    border: 1px solid #368D67;
    box-shadow: 0 0 0 0px #000;

    :hover {
      border-color: inherit !important;
    }
  }
  .dropdown__indicator {
    padding: 0px;
    width: 15px;
    color: #008FA6;
  }
  .dropdown__indicator-separator {
    width: 0px;
  }
  .dropdown__placeholder,
  .dropdown__single-value {
    font-size: calc(10px + (12 - 10) * ((100vw - 540px) / (1920 - 540)));
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.29;
    letter-spacing: normal;
    color: #368D67;
  }

  .dropdown__menu {
    & div {
      .css-1n7v3ny-option {
        background-color: transparent;
      }
    }
  }
`;
const DivText = styled.div`
  display:flex;
  flex-flow:row;
  @media screen and (max-width: 800px){
    flex-flow:column;
  }
`;
const Image = styled.img`
  width:80px;
  height:80px;
  object-fit:contain;
  margin-right:8px;
  @media screen and (max-width: 800px){
    margin-right:0;
    margin-bottom:8px;
  }
`;
const SectionHeading = styled.h3`
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #368D67;
  margin: 32px 0 8px;
`;
/* Three columns of prose do not fit a phone; scrolling the table inside its own
   box keeps the page itself from scrolling sideways. */
const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  margin: 16px 0 24px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  line-height: 20px;
  color: #5D6368;
  th, td{
    border: 1px solid #E6E8EA;
    padding: 12px;
    text-align: left;
    vertical-align: top;
    min-width: 180px;
  }
  th{
    font-weight: 600;
    background-color: #F5F7F8;
  }
`;
const Text = styled(Text12)`
  p{
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
  }
  ul{
    display: block;
    list-style-type: disc;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 40px;
  }
  li{
    display: list-item;
    margin-bottom: 8px;
  }
  h3{
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    color: #368D67;
    margin: 32px 0 8px;
  }
  a{
    color: #368D67;
    text-decoration: underline;
    word-break: break-word;
  }
  strong{
    font-weight: 600;
  }
  /* The processing-purposes table is three columns of prose and does not fit a
     phone. Scrolling it inside its own box keeps the page itself from
     scrolling sideways. */
  table{
    display: block;
    overflow-x: auto;
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0 24px;
  }
  th, td{
    border: 1px solid #E6E8EA;
    padding: 12px;
    text-align: left;
    vertical-align: top;
    min-width: 180px;
  }
  th{
    font-weight: 600;
    background-color: #F5F7F8;
    white-space: nowrap;
  }
`;